import { Button } from "@chakra-ui/react";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMessageSquare } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  type SourcingEventDto,
  SourcingEventStatus,
  useListSourcingEventFieldSourcesQuery,
} from "../../autogen/bff-api";
import { urls } from "../../urls";
import { useRunsPublicProcurements } from "../organization/useRunsPublicProcurements";
import { useSub } from "../subscription/useSub";
import { useOptionalAssistantContext } from "./AssistantProvider";
import { AssistantPanelContext } from "./assistantPanelContext";
import { PROCUREMENT_PARAM } from "./createProcurementParams";
import { interviewPosition } from "./creationAreas";

export const ContinueInterviewButton = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assistant = useOptionalAssistantContext();
  const panel = useContext(AssistantPanelContext);
  const sub = useSub();
  const runsPublicProcurements = useRunsPublicProcurements();
  const sources = useListSourcingEventFieldSourcesQuery({ eventId: event.id }).data;
  const [isOpening, setIsOpening] = useState(false);
  const conversationId = event.interview?.conversationId;
  const askToContinue = panel?.openWithQuestion;
  if (!conversationId || event.status !== SourcingEventStatus.Draft || !assistant || !askToContinue || !sources) {
    return null;
  }
  const position = interviewPosition(event, sources.fields, {
    hasPublicProcurementAccess: runsPublicProcurements,
    hasProductAccess: sub.hasProductAccess,
  });
  if (position.done) return null;

  const label = t("Continue with AI");
  const interview = `${urls.app.createProcurementWithAi()}&${PROCUREMENT_PARAM}=${encodeURIComponent(event.id)}`;
  const resume = async () => {
    setIsOpening(true);
    try {
      await assistant.switchTo(conversationId);
      navigate(interview);
    } catch {
      navigate(interview);
      askToContinue(t("Continue with AI for this procurement where it stopped."), label);
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <Button
      leftIcon={<FiMessageSquare />}
      variant="outline"
      colorScheme="teal"
      size="sm"
      isLoading={isOpening}
      isDisabled={assistant.isRunning}
      onClick={() => void resume()}
    >
      {label}
    </Button>
  );
};
